import type {
  WebsiteSite,
  WebsiteSiteInput,
  WebsiteVisitIntent,
  WebsiteVisitorAnalytics,
  WebsiteVisitorCompany,
  WebsiteVisitorPerson,
  WebsiteVisitorPrefs,
  WebsiteVisitorPrefsInput,
  WebsitePageview,
} from "@sales-ops-amplify/shared";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";

export function listWebsiteSites(): Promise<{ sites: WebsiteSite[] }> {
  return apiGet("/api/visitors/sites");
}

export function createWebsiteSite(
  input: WebsiteSiteInput,
): Promise<{ site: WebsiteSite; embed: string }> {
  return apiPost("/api/visitors/sites", input);
}

export function getWebsiteSite(
  siteId: string,
): Promise<{ site: WebsiteSite; embed: string }> {
  return apiGet(`/api/visitors/sites/${encodeURIComponent(siteId)}`);
}

export function updateWebsiteSite(
  siteId: string,
  patch: Partial<WebsiteSiteInput>,
): Promise<{ site: WebsiteSite }> {
  return apiPatch(`/api/visitors/sites/${encodeURIComponent(siteId)}`, patch);
}

export function deleteWebsiteSite(siteId: string): Promise<void> {
  return apiDelete(`/api/visitors/sites/${encodeURIComponent(siteId)}`);
}

export function testWebsiteConnection(siteId: string): Promise<{
  status: WebsiteSite["status"];
  lastSeenAt?: string;
  ok: boolean;
}> {
  return apiPost(
    `/api/visitors/sites/${encodeURIComponent(siteId)}/test-connection`,
    {},
  );
}

export function getWebsiteScript(siteId: string): Promise<{
  siteKey: string;
  embed: string;
  scriptUrl: string;
}> {
  return apiGet(`/api/visitors/script/${encodeURIComponent(siteId)}`);
}

export function listVisitorCompanies(params?: {
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
  intent?: WebsiteVisitIntent;
  q?: string;
}): Promise<{ companies: WebsiteVisitorCompany[] }> {
  return apiGet(
    "/api/visitors/companies",
    params as Record<string, string | undefined>,
  );
}

export function getVisitorCompany(key: string): Promise<{
  company: WebsiteVisitorCompany;
  pageviews: WebsitePageview[];
}> {
  return apiGet(`/api/visitors/companies/${encodeURIComponent(key)}`);
}

export function listVisitorPeople(params?: {
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
  intent?: WebsiteVisitIntent;
  q?: string;
}): Promise<{ people: WebsiteVisitorPerson[] }> {
  return apiGet(
    "/api/visitors/people",
    params as Record<string, string | undefined>,
  );
}

export function getVisitorPerson(leadId: string): Promise<{
  person: WebsiteVisitorPerson;
  pageviews: WebsitePageview[];
}> {
  return apiGet(`/api/visitors/people/${encodeURIComponent(leadId)}`);
}

export function getVisitorAnalytics(params?: {
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<WebsiteVisitorAnalytics> {
  return apiGet(
    "/api/visitors/analytics",
    params as Record<string, string | undefined>,
  );
}

export function getVisitorPreferences(): Promise<WebsiteVisitorPrefs> {
  return apiGet("/api/visitors/preferences");
}

export function updateVisitorPreferences(
  input: WebsiteVisitorPrefsInput,
): Promise<WebsiteVisitorPrefs> {
  return apiPut("/api/visitors/preferences", input);
}

export function seedVisitorSamples(): Promise<{
  pageviews: number;
  siteId: string;
}> {
  return apiPost("/api/visitors/seed-sample", {});
}
